import { UploadFileFiled } from '@/features/analytics/invite-links/ui/Marking/components/UploadFileFiled'
import Icon from '@/features/analytics/invite-links/ui/Marking/icon'
import { AnalyticsTableRowType } from '@/features/analytics/types'
import {
    useDeleteAct,
    useGenerateAct,
    useGetLinkedLinksByContract,
    useUpdateActNumber,
    useUpdateMarkingFormData,
    useUploadFinalActFile,
} from '@/shared/api'
import {
    useCreateCustomAct,
    useUpdateCustomAct,
} from '@/shared/api/ord/customDocuments.hooks'
import { apiQueryKey } from '@/shared/api/query-key'
import { QuestionSVGIcon } from '@/shared/assets'
import { Checkbox, DisableTooltip, Toast } from '@/shared/ui'
import { Label } from '@/shared/ui/label'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import { DocumentRecord, ORDMarkingFormData } from '@synapex/prisma/client'
import { WorkspaceWithLinkDescriptor } from '@synapex/shared2/entities'
import {
    ActUnion,
    ContractUnion,
    CustomActWithType,
    VkOrdHistoryResponse,
} from '@synapex/shared2/gateway-rest-api'
import { useIsMutating, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import CustomActForm, { CustomActFormValues } from './CustomActForm'
import { DraftActFileField } from './DraftActFileField'

type ActProps = {
    markingFormData: ORDMarkingFormData
    analyticsTableRowType: AnalyticsTableRowType
    openLinkedLinksModal: () => void
    acts: ActUnion[] | undefined
    linkedLinksCount: number
    vkOrdHistory: VkOrdHistoryResponse[]
    contract: ContractUnion | undefined
} & WorkspaceWithLinkDescriptor

const Act: React.FC<ActProps> = ({
    markingFormData,
    analyticsTableRowType,
    openLinkedLinksModal,
    acts,
    linkedLinksCount,
    vkOrdHistory,
    ...props
}) => {
    const [error, setError] = useState<'edo' | 'ord' | false>(false)
    const [customActFormOpen, setCustomActFormOpen] = useState(false)
    const [customActFormMode, setCustomActFormMode] = useState<
        'create' | 'edit'
    >('create')
    const [customActInitialValues, setCustomActInitialValues] = useState<
        CustomActFormValues | undefined
    >(undefined)
    const [editingCustomActId, setEditingCustomActId] = useState<number | null>(
        null
    )

    const queryClient = useQueryClient()

    const updateMarkingFormData = useUpdateMarkingFormData(props)

    const generateAct = useGenerateAct(props)

    const createCustomAct = useCreateCustomAct(props)
    const updateCustomAct = useUpdateCustomAct(props)

    const uploadActFile = useUploadFinalActFile(props)
    const deleteAct = useDeleteAct(props)
    const updateActNumber = useUpdateActNumber(props, {
        mutationKey: ['mutation-update-act-number'],
    })

    const globalUpdateActNumberPending = !!useIsMutating({
        mutationKey: ['mutation-update-act-number'],
    })

    const { data: linkedLinks } = useGetLinkedLinksByContract(
        props,
        markingFormData.draft_contract_id ??
            markingFormData.custom_contract_id!,
        markingFormData.custom_contract_id ? 'custom' : 'draft',
        {
            enabled:
                !!(
                    markingFormData.draft_contract_id ||
                    markingFormData.custom_contract_id
                ) && linkedLinksCount > 0,
        }
    )

    function handleCreateAct(type: 'default' | 'custom') {
        if (!markingFormData.ord_contragent_id) {
            toast.error('Сначала выберите рекламо-распространителя')
            return
        }

        if (type === 'default') {
            if (
                !markingFormData.draft_contract_id &&
                !markingFormData.custom_contract_id
            ) {
                toast.error('Сначала выберите договор')
                return
            }

            // if (!markingFormData.vat_percent) {
            //     toast.error('Сначала выберите НДС')
            //     return
            // }

            generateAct.mutate({
                ordContragentId: markingFormData.ord_contragent_id,
                contractId: markingFormData.draft_contract_id ?? undefined,
                customContractId:
                    markingFormData.custom_contract_id ?? undefined,
                vatPercent: markingFormData.vat_percent,
            })

            return
        }

        setCustomActFormMode('create')
        setCustomActInitialValues({
            number: '',
            date: new Date(),
            dateStart: new Date(autoDateStart),
            dateEnd: new Date(autoDateEnd),
            budget: autoBudget,
            is_budget_set_automatically: true,
            is_date_start_set_automatically: true,
            is_date_end_set_automatically: true,
        })
        setEditingCustomActId(null)
        setCustomActFormOpen(true)
    }

    async function handleUploadAct(file: File) {
        if (!markingFormData.ord_contragent_id) {
            toast.error('Сначала выберите рекламо-распространителя')
            return
        }

        if (!markingFormData.draft_act_id) {
            toast.error('Сначала выберите акт')
            return
        }

        uploadActFile.mutate({
            file,
            ordContragentId: markingFormData.ord_contragent_id,
            draftActId: markingFormData.draft_act_id,
        })
    }

    async function handleDeleteAct(
        type: 'draft' | 'final',
        contract: DocumentRecord
    ): Promise<void> {
        if (type === 'draft') {
            if (!markingFormData.ord_contragent_id) {
                return
            }
            await deleteAct.mutateAsync({
                documentRecordId: contract.documentRecordId,
                ordContragentId: markingFormData.ord_contragent_id,
            })
        } else {
            await updateMarkingFormData.mutateAsync(
                {
                    final_act_id: null,
                },
                {
                    onSuccess: () => {
                        void queryClient.invalidateQueries({
                            queryKey: apiQueryKey.markingFormData(),
                            predicate: (query) => {
                                const queryData = query.state.data as {
                                    draft_contract_id?: number
                                }
                                return (
                                    queryData.draft_contract_id ===
                                    markingFormData.draft_contract_id
                                )
                            },
                        })

                        void queryClient.invalidateQueries({
                            queryKey: apiQueryKey.acts(
                                props,
                                markingFormData.ord_contragent_id
                            ),
                        })
                    },
                }
            )
        }
    }

    function handleChangeCheckboxStatus(type: 'edo' | 'ord') {
        if (type === 'edo' && hasActFlag) {
            return
        }

        if (
            type === 'edo' &&
            markingFormData.act_edo_flag &&
            !actWaitingUpload
        ) {
            return
        }

        if (type === 'edo' && !hasDraftOrCustomAct) {
            setError(type)
            return
        }

        if (type === 'ord' && !canEnableActCheckbox) {
            setError(type)
            return
        }

        if (type === 'ord') {
            updateMarkingFormData.mutate(
                {
                    act_flag: !markingFormData.act_flag,
                },
                {
                    onSuccess: () => {
                        void queryClient.invalidateQueries({
                            queryKey: apiQueryKey.markingFormData(),
                            predicate: (query) => {
                                const queryData = query.state.data as {
                                    draft_contract_id?: number
                                }
                                return (
                                    queryData.draft_contract_id ===
                                    markingFormData.draft_contract_id
                                )
                            },
                        })

                        void queryClient.invalidateQueries({
                            queryKey: apiQueryKey.agencyContracts(props),
                        })
                    },
                }
            )
        } else {
            updateMarkingFormData.mutate(
                {
                    act_edo_flag: !markingFormData.act_edo_flag,
                },
                {
                    onSuccess: (data) => {
                        void queryClient.invalidateQueries({
                            queryKey: apiQueryKey.markingFormData(),
                            predicate: (query) => {
                                const queryData = query.state.data as {
                                    draft_contract_id?: number
                                }
                                return (
                                    queryData.draft_contract_id ===
                                    markingFormData.draft_contract_id
                                )
                            },
                        })

                        if (data.act_upload_scheduled_at && data.act_edo_flag) {
                            toast.success(
                                `Акт будет передан в ОРД ${new Date(data.act_upload_scheduled_at).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' })}`
                            )
                        }
                    },
                }
            )
        }

        setError(false)
    }

    function handleCustomActFormOpenChange(open: boolean) {
        setCustomActFormOpen(open)
        if (!open) {
            setCustomActFormMode('create')
            setCustomActInitialValues(undefined)
            setEditingCustomActId(null)
        }
    }

    function handleEditCustomAct() {
        if (!customAct) {
            toast.error('Не удалось найти кастомный акт для редактирования')
            return
        }

        setCustomActFormMode('edit')
        setCustomActInitialValues({
            number: customAct.number,
            date: new Date(customAct.date),
            dateStart: new Date(customAct.date_start),
            dateEnd: new Date(customAct.date_end),
            budget: customAct.budget,
            is_budget_set_automatically: customAct.is_budget_set_automatically,
            is_date_start_set_automatically:
                customAct.is_date_start_set_automatically,
            is_date_end_set_automatically:
                customAct.is_date_end_set_automatically,
        })
        setEditingCustomActId(customAct.custom_act_id)
        setCustomActFormOpen(true)
    }

    const toUtcDate = (date: Date) =>
        new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))

    async function handleSubmitCustomAct(data: CustomActFormValues) {
        if (!markingFormData.ord_contragent_id) {
            toast.error('Сначала выберите рекламо-распространителя')
            return
        }

        try {
            if (customActFormMode === 'edit' && editingCustomActId) {
                await updateCustomAct.mutateAsync({
                    customActId: editingCustomActId,
                    ad_distributor_contragent_id:
                        markingFormData.ord_contragent_id,
                    number: data.number,
                    date: toUtcDate(data.date),
                    date_start: toUtcDate(data.dateStart),
                    date_end: toUtcDate(data.dateEnd),
                    budget: data.budget,
                    is_budget_set_automatically:
                        data.is_budget_set_automatically,
                    is_date_start_set_automatically:
                        data.is_date_start_set_automatically,
                    is_date_end_set_automatically:
                        data.is_date_end_set_automatically,
                })

                toast.success('Кастомный акт обновлён')
            } else {
                const createdCustomAct = await createCustomAct.mutateAsync({
                    number: data.number,
                    date: toUtcDate(data.date),
                    date_start: toUtcDate(data.dateStart),
                    date_end: toUtcDate(data.dateEnd),
                    budget: data.budget,
                    is_budget_set_automatically:
                        data.is_budget_set_automatically,
                    is_date_start_set_automatically:
                        data.is_date_start_set_automatically,
                    is_date_end_set_automatically:
                        data.is_date_end_set_automatically,
                    ad_distributor_contragent_id:
                        markingFormData.ord_contragent_id,
                })

                await updateMarkingFormData.mutateAsync({
                    custom_act_id: createdCustomAct.custom_act_id,
                    draft_act_id: null,
                })

                toast.success('Кастомный акт создан')
            }
            handleCustomActFormOpenChange(false)
        } catch (error) {
            if (isAxiosError(error) && error.response?.data?.message) {
                toast.error(error.response.data.message)
            } else {
                toast.error(
                    customActFormMode === 'edit'
                        ? 'Не удалось обновить кастомный акт'
                        : 'Не удалось создать кастомный акт'
                )
            }
        }
    }

    async function handleDeleteCustomAct(): Promise<void> {
        if (!markingFormData.custom_act_id) {
            return
        }

        await updateMarkingFormData.mutateAsync(
            {
                custom_act_id: null,
            },
            {
                onSuccess: () => {
                    void queryClient.invalidateQueries({
                        queryKey: apiQueryKey.acts(
                            props,
                            markingFormData.ord_contragent_id
                        ),
                    })
                },
            }
        )
    }

    async function handleUpdateDefaultActNumber(
        documentRecordId: number,
        actNumber: string
    ): Promise<void> {
        if (!markingFormData.ord_contragent_id) {
            toast.error('Сначала выберите рекламо-распространителя')
            return
        }

        await updateActNumber.mutateAsync({
            ...props,
            documentRecordId,
            ordContragentId: markingFormData.ord_contragent_id,
            actNumber,
        })
    }

    const documentActs = useMemo(
        () =>
            acts?.filter(
                (act): act is Extract<ActUnion, { type: 'default' }> =>
                    act.type === 'default'
            ),
        [acts]
    )

    const customAct = useMemo(() => {
        if (!markingFormData.custom_act_id) {
            return undefined
        }

        return acts?.find(
            (act): act is CustomActWithType =>
                act.type === 'custom' &&
                act.custom_act_id === markingFormData.custom_act_id
        )
    }, [acts, markingFormData.custom_act_id])

    const finalAct = useMemo(() => {
        return documentActs?.find(
            (act) => act.documentRecordId === markingFormData.final_act_id
        )
    }, [documentActs, markingFormData.final_act_id])

    const draftAct = useMemo(() => {
        return documentActs?.find(
            (act) => act.documentRecordId === markingFormData.draft_act_id
        )
    }, [documentActs, markingFormData.draft_act_id])

    const hasDraftOrCustomAct =
        !!markingFormData.draft_act_id || !!markingFormData.custom_act_id

    const isEdoCheckboxDisabled = !hasDraftOrCustomAct

    const hasActFlag = markingFormData.act_flag
    const hasEdoFlag = markingFormData.act_edo_flag
    const hasCreativeFlag = markingFormData.creative_flag
    const allLinksPublished =
        linkedLinks?.links.every((link) => link.published_at) ?? false

    // Условия для активации чекбоксов и полей
    const canEnableActCheckbox =
        hasDraftOrCustomAct && (markingFormData.final_act_id || hasEdoFlag)

    // Проверяем что креатив принят в ЕРИР
    const isBlockedByCreativeERIR =
        vkOrdHistory.find((item) => item.type === 'creative')
            ?.lastUpdateStatus !== 'verified'

    const shouldDisableFields =
        hasActFlag ||
        !hasCreativeFlag ||
        !allLinksPublished ||
        isBlockedByCreativeERIR

    const autoDateStart = new Date(linkedLinks?.min_publish_date ?? new Date())
    const autoDateEnd = new Date(linkedLinks?.max_deleted_date ?? new Date())

    const contractStartDate =
        props.contract?.type === 'custom'
            ? new Date(props.contract.start_date)
            : new Date(props.contract?.created_at ?? new Date())

    const autoBudget =
        linkedLinks?.links.reduce((acc, link) => acc + link.budget, 0) ??
        analyticsTableRowType.budget ??
        0

    const actWaitingUpload = markingFormData.act_upload_scheduled_at
        ? new Date(markingFormData.act_upload_scheduled_at) >= new Date()
        : false

    const isEdoFlagLocked = markingFormData.act_edo_flag && !actWaitingUpload

    return (
        <>
            <div className="flex flex-col gap-[6px]">
                <div className="flex w-[448px] justify-between">
                    <div className="flex items-center gap-[6px]">
                        <Icon
                            icon="act"
                            checked={hasActFlag}
                            waiting={actWaitingUpload}
                        />

                        <h2 className="text-sm font-semibold">Акт</h2>

                        <Tooltip>
                            <TooltipTrigger>
                                <QuestionSVGIcon className="cursor-pointer" />
                            </TooltipTrigger>
                            <TooltipContent side="top">
                                <p>Акт</p>
                            </TooltipContent>
                        </Tooltip>
                    </div>
                    {linkedLinksCount > 1 && (
                        <div className="flex items-center gap-[6px]">
                            <a
                                className="cursor-pointer text-xs text-black/40 underline"
                                onClick={openLinkedLinksModal}
                            >
                                Связанные элементы
                            </a>
                            <div className="relative flex h-4 w-4 items-center justify-center overflow-hidden rounded-[20px] bg-blue-primary text-[10px] text-white">
                                {linkedLinksCount}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex items-end justify-between gap-8">
                    <div className="flex w-[448px] items-center justify-between">
                        <div className="relative flex h-[68px] flex-col justify-end gap-[6px]">
                            <span className="text-[10px] text-synapex-light-gray">
                                Акт
                            </span>
                            <div>
                                <DisableTooltip
                                    enabled={
                                        hasActFlag ||
                                        hasEdoFlag ||
                                        shouldDisableFields
                                    }
                                    message={
                                        hasActFlag || hasEdoFlag
                                            ? 'Если хотите отредактировать, то уберите галочку справа'
                                            : 'Нельзя сгенерировать акт пока креатив не обработан в ЕРИР'
                                    }
                                >
                                    <DraftActFileField
                                        onClickCreate={handleCreateAct}
                                        onClickEditCustomAct={
                                            handleEditCustomAct
                                        }
                                        onDeleteItem={handleDeleteAct}
                                        onDeleteCustomAct={
                                            handleDeleteCustomAct
                                        }
                                        onUpdateDefaultActNumber={
                                            handleUpdateDefaultActNumber
                                        }
                                        customAct={customAct}
                                        act={draftAct}
                                        disabled={
                                            shouldDisableFields || hasEdoFlag
                                        }
                                        error={Boolean(error)}
                                        isLoading={
                                            generateAct.isPending ||
                                            deleteAct.isPending ||
                                            createCustomAct.isPending ||
                                            updateCustomAct.isPending ||
                                            updateMarkingFormData.isPending ||
                                            updateActNumber.isPending ||
                                            globalUpdateActNumberPending ||
                                            uploadActFile.isPending
                                        }
                                        className="w-48"
                                    />
                                </DisableTooltip>
                            </div>
                        </div>

                        <div className="flex h-[68px] flex-col justify-end gap-[6px]">
                            <span className="text-[10px] text-synapex-light-gray">
                                Подписанный акт
                            </span>

                            <DisableTooltip
                                enabled={hasActFlag}
                                message="Если хотите отредактировать, то уберите галочку справа"
                            >
                                <UploadFileFiled
                                    selectedItem={finalAct || null}
                                    onDeleteItem={() => {
                                        if (finalAct) {
                                            void handleDeleteAct(
                                                'final',
                                                finalAct
                                            )
                                        }
                                    }}
                                    onFileChange={handleUploadAct}
                                    placeholder="Загрузить подписанный акт"
                                    setSelectedItem={() => {}}
                                    disabled={!markingFormData.draft_act_id}
                                    error={error === 'ord'}
                                    isLoading={
                                        uploadActFile.isPending ||
                                        updateMarkingFormData.isPending
                                    }
                                    className="w-48"
                                />
                            </DisableTooltip>
                        </div>

                        <div className="relative flex h-[68px] flex-col items-center justify-end gap-[6px]">
                            <span className="w-[60px] break-words text-center text-[10px] font-medium leading-3">
                                Отправить в ОРД
                            </span>
                            <DisableTooltip
                                enabled={
                                    isEdoCheckboxDisabled ||
                                    hasActFlag ||
                                    isEdoFlagLocked
                                }
                                message={
                                    isEdoFlagLocked
                                        ? 'Акт уже отправлен в ОРД, отменить нельзя'
                                        : hasActFlag
                                          ? 'Если хотите отредактировать, то уберите галочку справа'
                                          : 'Пожалуйста, заполните все параметры, чтобы отметить этот пункт'
                                }
                            >
                                <div
                                    onClick={() => {
                                        if (
                                            isEdoCheckboxDisabled ||
                                            hasActFlag ||
                                            isEdoFlagLocked
                                        ) {
                                            handleChangeCheckboxStatus('edo')
                                        }
                                    }}
                                    className="flex h-8 items-center"
                                >
                                    <Checkbox
                                        checked={markingFormData.act_edo_flag}
                                        disabled={
                                            isEdoCheckboxDisabled ||
                                            hasActFlag ||
                                            isEdoFlagLocked
                                        }
                                        onChange={() =>
                                            handleChangeCheckboxStatus('edo')
                                        }
                                        sizeVariant="big"
                                        variant={'green'}
                                        size={20}
                                    />
                                </div>
                            </DisableTooltip>
                        </div>
                    </div>

                    <div className="flex w-[105px] items-center gap-[6px] pb-[5px]">
                        <DisableTooltip
                            enabled={!canEnableActCheckbox}
                            className="h-5"
                            message="Пожалуйста, заполните все параметры, чтобы отметить этот пункт"
                        >
                            <div
                                onClick={() => {
                                    if (!canEnableActCheckbox) {
                                        handleChangeCheckboxStatus('ord')
                                    }
                                }}
                            >
                                <Checkbox
                                    id="act_allow"
                                    checked={hasActFlag}
                                    disabled={!canEnableActCheckbox}
                                    onChange={() =>
                                        handleChangeCheckboxStatus('ord')
                                    }
                                    sizeVariant="big"
                                    variant={'green'}
                                    size={20}
                                />
                            </div>
                        </DisableTooltip>
                        <Label
                            htmlFor="act_allow"
                            className="w-[79px] text-[10px] font-medium leading-3"
                        >
                            Акт подписан
                        </Label>
                    </div>
                </div>
            </div>
            <CustomActForm
                open={customActFormOpen}
                setOpen={handleCustomActFormOpenChange}
                onSubmit={handleSubmitCustomAct}
                mode={customActFormMode}
                initialValues={customActInitialValues}
                isSubmitting={
                    createCustomAct.isPending || updateCustomAct.isPending
                }
                autoDateStart={autoDateStart}
                autoDateEnd={autoDateEnd}
                autoBudget={autoBudget}
                contractStartDate={contractStartDate}
                isLinkDeleted={!!linkedLinks?.max_deleted_date}
            />
        </>
    )
}

export default Act
